/**
 * Boot-time wiring for the Android / iOS app (Capacitor). No-op on the web.
 *
 *  - viewport: content stays inside the safe areas (no `viewport-fit=cover`) and inputs don't zoom
 *    on focus — on Android `SystemBars.insetsHandling: native` follows this meta tag.
 *  - status bar icons follow the colour scheme.
 *  - Android back button: closes the open dialog / menu first, then goes back, and at the first
 *    screen sends the app to the background instead of killing it.
 *  - file downloads (PDF / Excel / CSV exports) go through the native share sheet — see downloads.js.
 *  - API requests carry the stored workspace as their tenant, the stock microfrontends' included —
 *    see tenantRequests.js.
 */
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isNativeApp, nativePlatform } from './platform';
import { installNativeDownloads } from './downloads';
import { installTenantRequests } from './tenantRequests';

const NATIVE_VIEWPORT = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=contain';

function applyViewport() {
  let meta = document.querySelector('meta[name="viewport"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'viewport';
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', NATIVE_VIEWPORT);
}

function syncStatusBar() {
  const dark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
  // Style.Dark = light icons for a dark background.
  StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
}

/** An open MUI dialog / drawer / menu / popover, topmost last. */
const openOverlays = () => Array.from(document.querySelectorAll('.MuiModal-root:not(.MuiModal-hidden)'));

/**
 * Close the topmost overlay the way the user would on the web (Escape), so dialogs keep their own
 * close logic (unsaved-changes confirms, `disableEscapeKeyDown`). Returns whether one was open.
 */
function closeTopOverlay() {
  const overlays = openOverlays();
  if (!overlays.length) return false;
  const top = overlays[overlays.length - 1];
  const target = top.contains(document.activeElement) ? document.activeElement : top.querySelector('[tabindex], [role="dialog"], [role="presentation"]') || top;
  target.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true }));
  return true;
}

function handleBackButton({ canGoBack }) {
  if (closeTopOverlay()) return;
  if (canGoBack) window.history.back();
  else CapacitorApp.minimizeApp().catch(() => {});
}

let installed = false;

export function initNativeShell() {
  if (installed || !isNativeApp()) return;
  installed = true;

  installTenantRequests();
  document.documentElement.classList.add('native-app', `native-${nativePlatform()}`);
  applyViewport();
  installNativeDownloads();

  syncStatusBar();
  window.matchMedia?.('(prefers-color-scheme: dark)')?.addEventListener?.('change', syncStatusBar);

  if (nativePlatform() === 'android') {
    CapacitorApp.addListener('backButton', handleBackButton).catch?.(() => {});
  }
}
